import React from "react";
import { Box, Flex, Icon, Image, Text } from "@chakra-ui/react";
import { HiOutlineDocument, HiOutlinePhoto, HiOutlineXMark } from "react-icons/hi2";
import { useDraggable } from "../useDraggable";
import { c } from "../../classroomColors";

/**
 * A page or picture opened onto the board.
 *
 * It is a board item like a note: stored with the board, so every screen in
 * the room shows it, it moves and resizes like anything else, Delete takes
 * it off, and undo puts it back — for everyone. The picture is an uploaded
 * image (a PDF arrives as its pages), so nothing here needs a PDF reader.
 *
 * The page itself, no card round it, with a small label riding above its
 * top edge saying what it is.
 */
const ContentItem = ({ item, isSelected, readOnly, onMove, onSelect, onRemove }) => {
  const { onPointerDown } = useDraggable({
    x: item.x,
    y: item.y,
    onMove,
    onStart: onSelect,
    disabled: readOnly,
  });
  const width = item.width || 420;
  const height = item.height || Math.round(width * 1.414);
  const label = `${item.name || "Content"}${item.page ? ` · page ${item.page}` : ""}`;

  return (
    <Box
      role="group"
      position="absolute"
      left={`${item.x}px`}
      top={`${item.y}px`}
      width={`${width}px`}
      height={`${height}px`}
      onPointerDown={onPointerDown}
      cursor={readOnly ? "default" : "grab"}
      _active={{ cursor: readOnly ? "default" : "grabbing" }}
      data-content-item
      aria-label={label}
    >
      <Flex
        position="absolute"
        bottom="100%"
        left="0"
        maxW="100%"
        mb="7px"
        align="center"
        gap="6px"
        pl="8px"
        pr={readOnly ? "10px" : "4px"}
        h="24px"
        borderRadius="200px"
        bg={c.page}
        border="1px solid"
        borderColor={c.borderSoft}
      >
        <Icon as={item.page ? HiOutlineDocument : HiOutlinePhoto} boxSize="12px" color={c.textSub} flexShrink={0} />
        <Text fontSize="11px" color={c.textSub} noOfLines={1}>
          {label}
        </Text>
        {!readOnly && onRemove && (
          <Flex
            as="button"
            type="button"
            data-no-drag
            aria-label={`Remove ${label}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onRemove}
            boxSize="18px"
            flexShrink={0}
            align="center"
            justify="center"
            borderRadius="full"
            color={c.textMuted}
            opacity={isSelected ? 1 : 0}
            _groupHover={{ opacity: 1 }}
            _focusVisible={{ opacity: 1 }}
            _hover={{ bg: c.hover, color: c.text }}
          >
            <Icon as={HiOutlineXMark} boxSize="12px" />
          </Flex>
        )}
      </Flex>

      {/* The page: paper-white whatever the theme. */}
      <Box
        w="100%"
        h="100%"
        bg="white"
        borderRadius="4px"
        overflow="hidden"
        boxShadow={
          isSelected
            ? "0 1px 2px rgba(0,0,0,0.06), 0 16px 36px -16px rgba(0,0,0,0.36)"
            : "0 1px 2px rgba(0,0,0,0.06), 0 12px 32px -16px rgba(0,0,0,0.28)"
        }
        outline="1px solid rgba(34,34,34,0.08)"
      >
        <Image src={item.url} alt={label} w="100%" h="100%" objectFit="contain" draggable={false} />
      </Box>
    </Box>
  );
};

export default ContentItem;
